import React, { useState, useContext, useEffect, useRef } from 'react';
import { useStyletron } from 'baseui';
import { Input } from 'baseui/input';
import { Button } from 'baseui/button';
import { DatePicker } from 'baseui/datepicker';
import { AppContext, AjaxContext } from 'commons-ui';

const SUBMIT_ENDPOINT = '/dashboard/unauthorized/talkzyData-talkzyData-submitTalkzyExport';
const STATUS_ENDPOINT = '/dashboard/unauthorized/talkzyData-talkzyData-getTalkzyExportStatus';
const POLL_INTERVAL_MS = 3000;
const MAX_POLL_ATTEMPTS = 120; // ~6 minutes

type TStatus = 'QUEUED' | 'RUNNING' | 'COMPLETED' | 'FAILED' | null;

const TalkzyDataDashboard: React.FC<{}> = () => {
	const [css] = useStyletron();
	const { postData } = useContext(AjaxContext);
	const { app } = useContext(AppContext);
	const appName = app?.appName;

	const [phone, setPhone] = useState<string>('');
	const [dateRange, setDateRange] = useState<Date[]>([]);
	const [jobId, setJobId] = useState<string | null>(null);
	const [status, setStatus] = useState<TStatus>(null);
	const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
	const [recordCount, setRecordCount] = useState<number | null>(null);
	const [truncated, setTruncated] = useState<boolean>(false);
	const [resolvedWindow, setResolvedWindow] = useState<{ from?: string; to?: string }>({});
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [message, setMessage] = useState<{ text: string; isError: boolean } | null>(null);

	const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const attemptsRef = useRef<number>(0);

	const isProcessing: boolean = !!jobId && status !== 'COMPLETED' && status !== 'FAILED';

	const clearPolling = () => {
		if (pollRef.current) {
			clearInterval(pollRef.current);
			pollRef.current = null;
		}
	};

	// Stop polling if the user navigates away.
	useEffect(() => () => clearPolling(), []);

	const formatDate = (iso?: string): string => {
		if (!iso) return '';
		const d = new Date(iso);
		return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
	};

	const resetResult = () => {
		setJobId(null);
		setStatus(null);
		setDownloadUrl(null);
		setRecordCount(null);
		setTruncated(false);
		setResolvedWindow({});
		setMessage(null);
		attemptsRef.current = 0;
		clearPolling();
	};

	const startPolling = (id: string) => {
		clearPolling();
		attemptsRef.current = 0;
		pollRef.current = setInterval(async () => {
			attemptsRef.current += 1;
			try {
				const res: any = await postData({
					endpoint: STATUS_ENDPOINT,
					queryParams: { appName },
					body: { jobId: id }
				});
				const data: any = res?.data?.data || {};
				setStatus(data.status);
				if (data.status === 'COMPLETED') {
					setDownloadUrl(data.downloadUrl);
					setRecordCount(typeof data.recordCount === 'number' ? data.recordCount : null);
					setTruncated(Boolean(data.truncated));
					clearPolling();
					setMessage({ text: 'Your file is ready to download.', isError: false });
				} else if (data.status === 'FAILED') {
					clearPolling();
					setMessage({ text: data.error || 'Export failed. Please try again.', isError: true });
				} else if (attemptsRef.current >= MAX_POLL_ATTEMPTS) {
					clearPolling();
					setMessage({ text: 'This is taking longer than expected. Please try again later.', isError: true });
				}
			} catch (err: any) {
				clearPolling();
				setMessage({ text: err?.response?.data?.error || 'Failed to fetch export status.', isError: true });
			}
		}, POLL_INTERVAL_MS);
	};

	const handleSubmit = async () => {
		resetResult();
		setIsSubmitting(true);
		try {
			const body: any = {};
			if (phone.trim()) body.phone = phone.trim();
			if (dateRange[0]) body.from = dateRange[0].toISOString();
			if (dateRange[1]) body.to = dateRange[1].toISOString();
			const res: any = await postData({ endpoint: SUBMIT_ENDPOINT, queryParams: { appName }, body });
			const newJobId: string | undefined = res?.data?.jobId;
			if (!newJobId) {
				throw new Error(res?.data?.error || 'Failed to start export');
			}
			setJobId(newJobId);
			setStatus('QUEUED');
			setResolvedWindow({ from: res?.data?.from, to: res?.data?.to });
			startPolling(newJobId);
		} catch (err: any) {
			setMessage({ text: err?.response?.data?.error || err?.message || 'Failed to start export.', isError: true });
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDownload = () => {
		if (downloadUrl) {
			window.open(downloadUrl, '_blank');
		}
	};

	return (
		<div className={css({ padding: '24px', maxWidth: '560px' })}>
			<h2 className={css({ marginBottom: '8px' })}>Talkzy Data Export</h2>
			<p className={css({ marginTop: '0px', color: '#666' })}>
				Export Talkzy gameplay chats as a CSV. Enter a phone for one user, or leave blank for all users.
			</p>

			<div className={css({ marginBottom: '16px' })}>
				<label className={css({ display: 'block', marginBottom: '4px', fontWeight: 600 })}>Phone number (optional)</label>
				<Input
					value={phone}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPhone(e.currentTarget.value)}
					placeholder='Leave blank to export all users'
					disabled={isSubmitting || isProcessing}
				/>
			</div>

			<div className={css({ marginBottom: '16px' })}>
				<label className={css({ display: 'block', marginBottom: '4px', fontWeight: 600 })}>Date range (optional)</label>
				<DatePicker
					range
					value={dateRange}
					onChange={({ date }) => {
						const next = Array.isArray(date) ? date : [date];
						setDateRange(next.filter((d): d is Date => Boolean(d)));
					}}
					placeholder='Defaults to the last 2 days'
					disabled={isSubmitting || isProcessing}
				/>
			</div>

			<Button onClick={handleSubmit} isLoading={isSubmitting} disabled={isProcessing}>
				Generate CSV
			</Button>

			{(resolvedWindow.from || resolvedWindow.to) && (
				<div className={css({ marginTop: '12px', fontSize: '13px', color: '#666' })}>
					Window served: {formatDate(resolvedWindow.from)} → {formatDate(resolvedWindow.to)}
				</div>
			)}

			{isProcessing && (
				<div className={css({ marginTop: '20px', color: '#444' })}>
					Processing… this can take a minute. Please keep this page open.
				</div>
			)}

			{message && (
				<div className={css({ marginTop: '16px', color: message.isError ? '#EE4443' : '#008000' })}>{message.text}</div>
			)}

			{downloadUrl && (
				<div className={css({ marginTop: '16px' })}>
					<Button onClick={handleDownload}>Download CSV</Button>
					{recordCount !== null && (
						<div className={css({ marginTop: '8px', fontSize: '13px', color: '#666' })}>
							{recordCount} row{recordCount === 1 ? '' : 's'}
							{truncated ? ' (capped at the row limit — more rows may exist)' : ''}
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default TalkzyDataDashboard;
